import React, {Suspense, useEffect, lazy} from 'react';
import PropTypes from 'prop-types';
import {matchPath, useRoutes, useNavigate, useLocation} from 'react-router-dom';
import {multiPathRoutes, notFoundRoute} from '~/router/routerHelpers';
import classnames from 'classnames';
import {CSSTransition, TransitionGroup} from 'react-transition-group';

import './ModalRoutes.scss';

const AdditionalPrintAreas = lazy(
  () => import('~/components/AdditionalPrintAreas'),
  'AdditionalPrintAreas'
);
const AddItem = lazy(() => import('~/components/AddItem'), 'AddItem');
const NamesAndNumbersList = lazy(
  () => import('~/components/NamesAndNumbersList'),
  'NamesAndNumbersList'
);
const NamesAndNumbersQuantities = lazy(
  () => import('~/components/NamesAndNumbersQuantities'),
  'NamesAndNumbersQuantities'
);
const NewSleevePrintPricing = lazy(
  () => import('~/components/NewSleevePrintPricing'),
  'NewSleevePrintPricing'
);
const ProductCatalog = lazy(
  () => import('~/components/ProductCatalog'),
  'ProductCatalog'
);
const SaveConfirmation = lazy(
  () => import('~/components/SaveConfirmation'),
  'SaveConfirmation'
);
const SaveForm = lazy(() => import('~/components/SaveForm'), 'SaveForm');

// Eager loaded components
import ContentCard from '~/components/ContentCard';

export const modalRoutePaths = [
  '/additionalPrintArea/sleeve',
  '/additionalPrintArea/hood',
  '/additionalPrintArea/leg',
  '/addItems/:step',
  '/addItems/:step/:styleId',
  '/addItems/:step/:styleId/:colorId',
  '/namesAndNumbers/list',
  '/namesAndNumbers/list',
  '/namesAndNumbers/quantities',
  '/productCatalog',
  '/productCatalog/:category',
  '/productCatalog/:category/:id',
  '/productCatalogOrderMore/:id',
  '/productCatalogMultiItem',
  '/namesAndNumbers/saveConfirmation',
  '/namesAndNumbers/saveForm',
  '/sleevePrintPricing'
];

const AssistedDesignFallBack = () => (
  <div className="ndx-ModalRoutes">
    <div className="ndx-ModalRoutes-container">
      <ContentCard />
    </div>
  </div>
);

const propTypes = {
  anchorStyleHasSleeveView: PropTypes.bool,
  handleAddItemBrowseCatalog: PropTypes.func,
  handleAddItemOnClose: PropTypes.func,
  handleAddItemPickProduct: PropTypes.func,
  handleSwitcherStatusChange: PropTypes.func,
  isFundraisingMode: PropTypes.bool,
  meta: PropTypes.objectOf(PropTypes.func)
};

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const ModalRoutesLg = props => {
  const navigate = useNavigate();
  const location = useLocation();
  const {handleSwitcherStatusChange} = props;

  const wrap = (component, promo, specialDesignsAreas) => {
    const modalSwitchClasses = classnames(
      'ndx-ModalRoutes',
      {
        'ndx-ModalRoutes-isPromo': promo
      },
      {'ndx-ModalRoutes-isSpecialDesigns': specialDesignsAreas}
    );
    return component ? (
      <div
        key={`overlay${component.key}`}
        className={modalSwitchClasses}
        style={{display: 'block'}}>
        <div className="ndx-ModalRoutes-container">{component}</div>
      </div>
    ) : null;
  };

  useEffect(() => {
    if (location.pathname) {
      const isModalPath = () => {
        return modalRoutePaths.some(
          path => !!matchPath(path, location.pathname)
        );
      };
      handleSwitcherStatusChange(isModalPath(), location.pathname);
    }
  }, [handleSwitcherStatusChange, location.pathname]);

  const routesConfig = [
    {
      path: '/additionalPrintArea/*',
      element: <AdditionalPrintAreas />
    },
    ...multiPathRoutes({
      path: [
        '/addItems',
        '/addItems/:step',
        '/addItems/:step/:styleId',
        '/addItems/:step/:styleId/:colorId'
      ],
      element: wrap(
        <AddItem
          animation
          wide
          isFundraisingMode={props.isFundraisingMode}
          onBrowseCatalog={props.handleAddItemBrowseCatalog}
          onClose={props.handleAddItemOnClose}
          onPickProduct={props.handleAddItemPickProduct}
        />
      )
    }),
    {
      path: 'productCatalog',
      children: [
        {
          index: true,
          element: wrap(<ProductCatalog animationComplete />)
        },
        {
          path: ':category',
          children: [
            {
              index: true,
              element: wrap(<ProductCatalog animationComplete />)
            },
            {
              path: ':id',
              element: wrap(<ProductCatalog animationComplete />)
            }
          ]
        }
      ]
    },
    {
      path: '/productCatalogOrderMore/:id',
      element: wrap(<ProductCatalog animationComplete orderMore />)
    },
    {
      path: 'productCatalogMultiItem',
      element: wrap(<ProductCatalog animationComplete isMultiItem />)
    },
    {
      path: 'sleevePrintPricing',
      element: wrap(
        <NewSleevePrintPricing onShowTag={props.meta.sleevePrintModal} />
      )
    }
  ];

  const slideUpRoutes = (
    <TransitionGroup component={null}>
      <CSSTransition
        key={location.key}
        classNames="modal-routes-slide-up"
        timeout={300}>
        <Suspense fallback={<div />}>
          {useRoutes([
            {
              path: 'namesAndNumbers',
              children: [
                {
                  path: 'list',
                  element: wrap(<NamesAndNumbersList animation />)
                },
                {
                  path: 'quantities',
                  element: wrap(<NamesAndNumbersQuantities animation />)
                },
                {
                  path: 'saveConfirmation',
                  element: wrap(<SaveConfirmation />)
                },
                {
                  path: 'saveForm',
                  element: wrap(<SaveForm onClose={() => navigate('/')} />)
                }
              ]
            },
            notFoundRoute
          ])}
        </Suspense>
      </CSSTransition>
    </TransitionGroup>
  );

  const contentCardRoutes = (
    <Suspense fallback={<AssistedDesignFallBack />}>
      {useRoutes(routesConfig)}
    </Suspense>
  );

  return (
    <>
      {contentCardRoutes}
      {slideUpRoutes}
    </>
  );
};

ModalRoutesLg.displayName = 'ModalRoutesLg';
ModalRoutesLg.propTypes = propTypes;

export default ModalRoutesLg;
